import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { logAuditEvent, saveUploadHistory } from "../../../api/api-hook";
import UploadHistory from "./UploadHistory";
import { getAdminSession } from "../../../utils/adminAuth";

const HOST = process.env.REACT_APP_HOST_URL || "";
const BRAND_GREEN = "#023822";

// ── Template definition ───────────────────────────────────────────────────────

const TEMPLATE_HEADERS = [
  "first_name", "last_name", "email", "phone",
  "date_of_birth", "gender", "address", "address_2",
  "city", "state", "zip", "ghin_number",
  "vip_package", "vip_card_type",
  "cart_total", "payment_method",
  "transaction_number", "check_number",
  "bag_tag", "bag_tag_name", "bag_tag_price",
  "donation", "donation_amount",
  "contact_id",
];

// Maps each template column name to the property key in a normalizeRow() result
const HEADER_TO_KEY: Record<string, string> = {
  first_name:          "firstName",
  last_name:           "lastName",
  email:               "email",
  phone:               "phone",
  date_of_birth:       "date_of_birth",
  gender:              "sex",
  address:             "address",
  address_2:           "street_address_2",
  city:                "city",
  state:               "state",
  zip:                 "zip",
  ghin_number:         "ghinNumber",
  vip_package:         "vip_package",
  vip_card_type:       "vip_card_type",
  cart_total:          "cart_total",
  payment_method:      "payment_method",
  transaction_number:  "versapayTransactionId",
  check_number:        "check_number",
  bag_tag:             "bagTag",
  bag_tag_name:        "personalization",
  bag_tag_price:       "bagTagPrice",
  donation:            "donation",
  donation_amount:     "donation_amount",
  contact_id:          "id",
};

const TEMPLATE_NOTES = [
  "Required", "Required", "Optional", "e.g. (555) 555-5555",
  "MM/DD/YYYY", "M or F", "", "",
  "", "2-letter code e.g. VA", "5-digit ZIP", "GHIN number",
  "vip / bronze / silver / gold / bronze-18-25 / wv-vip-card / vip-replacement / bagtag",
  "Digital or Paper",
  "Required – numeric e.g. 99.00", "Credit Card / Check / Cash / Miscellaneous",
  "CC transaction ID", "Check # (if payment_method = Check)",
  "TRUE or FALSE", "Bag tag personalization text", "Retail price incl. tax e.g. 25.00",
  "TRUE or FALSE", "Donation amount e.g. 10.00",
  "HubSpot contact ID (leave blank for new contacts)",
];

const EXAMPLE_ROW = [
  "Jane", "Doe", "jane.doe@example.com", "(804) 555-1234",
  "01/15/1985", "F", "123 Main St", "Apt 4",
  "Richmond", "VA", "23220", "1234567",
  "bronze", "Digital",
  "99.00", "Credit Card",
  "", "",
  "FALSE", "", "",
  "FALSE", "",
  "",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, TEMPLATE_NOTES, EXAMPLE_ROW]);

  // Format zip column as text (preserves leading zeros); z:"@" on both col and existing cells
  const zipColIdx = TEMPLATE_HEADERS.indexOf("zip");
  ws["!cols"] = TEMPLATE_HEADERS.map((_, i) =>
    i === zipColIdx ? { wch: 22, z: "@" } : { wch: 22 }
  );
  for (let r = 0; r < 3; r++) {
    const ref = XLSX.utils.encode_cell({ r, c: zipColIdx });
    if (ws[ref]) { ws[ref].t = "s"; ws[ref].z = "@"; }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Orders");
  XLSX.writeFile(wb, "vsga_order_upload_template.xlsx");
}

function parseBool(val: any): boolean {
  if (typeof val === "boolean") return val;
  return String(val || "").trim().toLowerCase() === "true";
}

const VIP_CARD_TYPE_OPTIONS  = ["Digital", "Paper"];
const PAYMENT_METHOD_OPTIONS = ["Credit Card", "Check", "Cash", "Miscellaneous"];

function matchEnum(val: string, options: string[]): string {
  if (!val) return "";
  const lower = val.trim().toLowerCase();
  return options.find((o) => o.toLowerCase() === lower) ?? val.trim();
}

function normalizeRow(raw: any): Record<string, any> {
  return {
    firstName:         String(raw.first_name  || raw.firstName  || "").trim(),
    lastName:          String(raw.last_name   || raw.lastName   || "").trim(),
    email:             String(raw.email       || "").trim().toLowerCase(),
    phone:             String(raw.phone       || "").trim(),
    date_of_birth:     String(raw.date_of_birth || "").trim(),
    sex:               String(raw.gender      || raw.sex        || "").trim(),
    address:           String(raw.address     || "").trim(),
    street_address_2:  String(raw.address_2   || raw.street_address_2 || "").trim(),
    city:              String(raw.city        || "").trim(),
    state:             String(raw.state       || "").trim(),
    zip:               String(raw.zip         || "").trim(),
    ghinNumber:        String(raw.ghin_number  || raw.ghinNumber || "").trim(),
    vip_package:       String(raw.vip_package  || "").trim().toLowerCase(),
    vip_card_type:     matchEnum(String(raw.vip_card_type || ""), VIP_CARD_TYPE_OPTIONS),
    cart_total:        Number(raw.cart_total   || 0),
    payment_method:    matchEnum(String(raw.payment_method || "Credit Card"), PAYMENT_METHOD_OPTIONS),
    versapayTransactionId: String(raw.transaction_number || "").trim(),
    check_number:      String(raw.check_number || "").trim(),
    bagTag:            parseBool(raw.bag_tag),
    personalization:   String(raw.bag_tag_name || raw.personalization || "").trim(),
    bagTagPrice:       Number(raw.bag_tag_price || 0),
    donation:          parseBool(raw.donation),
    donation_amount:   Number(raw.donation_amount || 0),
    id:                String(raw.contact_id  || raw.id || "").trim() || undefined,
    quantity:          "1",
    sameAsBilling:     true,
    GiftPurchase:      false,
    GiftProductId:     "",
    GiftVipPackage:    "",
  };
}

// Packages where we skip price validation (no fixed price or special handling)
const SKIP_PRICE_VALIDATION = new Set(["vip-replacement", "bagtag", "wv-vip-card"]);

function validateRow(row: Record<string, any>, productPrices: Record<string, number>): string | null {
  if (!row.firstName) return "first_name is required";
  if (!row.lastName)  return "last_name is required";
  if (row.email && !/\S+@\S+\.\S+/.test(row.email)) return "email format is invalid";
  if (!row.vip_package) return "vip_package is required";
  if (row.cart_total < 0) return "cart_total must be >= 0";
  if (row.vip_card_type && !VIP_CARD_TYPE_OPTIONS.includes(row.vip_card_type))
    return `vip_card_type must be one of: ${VIP_CARD_TYPE_OPTIONS.join(", ")}`;
  if (row.payment_method && !PAYMENT_METHOD_OPTIONS.includes(row.payment_method))
    return `payment_method must be one of: ${PAYMENT_METHOD_OPTIONS.join(", ")}`;

  // Price validation: cart_total must match the package unit price from HubSpot
  const pkg = (row.vip_package || "").toLowerCase();
  if (!SKIP_PRICE_VALIDATION.has(pkg) && Object.keys(productPrices).length > 0) {
    const pkgPrice = productPrices[pkg];
    if (pkgPrice !== undefined) {
      const actual = Number(row.cart_total || 0);
      if (Math.abs(actual - pkgPrice) > 0.02) {
        return `cart_total $${actual.toFixed(2)} doesn't match ${pkg} package price $${pkgPrice.toFixed(2)}`;
      }
    }
  }

  return null;
}

async function upsertRow(row: Record<string, any>): Promise<void> {
  const resp = await axios.post(`${HOST}/api/member/upsert`, { memberData: { ...row, created_source: "Bulk Upload" } }, { timeout: 60000 });
  if (!resp.data?.success) {
    throw new Error(resp.data?.error || "Server returned failure");
  }
}

function extractErrorMessage(err: any): string {
  // Prefer the structured summary the server built from HubSpot field errors
  const serverError = err?.response?.data?.error;
  if (serverError) return serverError;
  return err?.message || "Unknown error";
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface RowResult {
  rowIndex: number;
  email: string;
  name: string;
  success: boolean;
  error?: string;
  rawRow: Record<string, any>;
}

// ── Component ─────────────────────────────────────────────────────────────────

const BulkUpload = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName,       setFileName]       = useState("");
  const [parsedRows,     setParsedRows]     = useState<Record<string, any>[]>([]);
  const [parseError,     setParseError]     = useState<string | null>(null);
  const [uploading,      setUploading]      = useState(false);
  const [progress,       setProgress]       = useState({ current: 0, total: 0 });
  const [results,        setResults]        = useState<RowResult[]>([]);
  const [done,           setDone]           = useState(false);
  const [productPrices,  setProductPrices]  = useState<Record<string, number>>({});
  const [historyKey,     setHistoryKey]     = useState(0);

  // Fetch product prices once on mount so we can validate cart_total per row
  useEffect(() => {
    axios.get(`${HOST}/api/vip/products`).then((resp) => {
      const prices: Record<string, number> = {};
      for (const p of (resp.data || [])) {
        if (p.tabKey && p.price != null) prices[p.tabKey.toLowerCase()] = Number(p.price);
      }
      setProductPrices(prices);
    }).catch(() => {/* non-fatal — validation simply skips if prices unavailable */});
  }, []);

  const failures = results.filter((r) => !r.success);
  const successes = results.filter((r) => r.success);

  // ── File parsing ────────────────────────────────────────────────────────────

  const handleFile = (file: File) => {
    setParseError(null); setParsedRows([]); setResults([]); setDone(false);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
        // Skip note row if first cell looks like a notes row (contains "Required")
        const firstRow = raw[0] || {};
        const filtered = Object.values(firstRow).some((v) =>
          String(v).toLowerCase().includes("required") || String(v).toLowerCase().includes("e.g.")
        ) ? raw.slice(1) : raw;
        if (filtered.length === 0) { setParseError("No data rows found in file."); return; }
        if (filtered.length > 500) { setParseError("Maximum 500 rows per upload."); return; }
        setParsedRows(filtered.map(normalizeRow));
      } catch (err: any) {
        setParseError("Could not parse file: " + (err?.message || "unknown error"));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  // ── Upload ──────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!parsedRows.length || uploading) return;
    setUploading(true); setResults([]); setDone(false);
    setProgress({ current: 0, total: parsedRows.length });

    const batch: RowResult[] = [];

    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];
      const validationErr = validateRow(row, productPrices);
      if (validationErr) {
        batch.push({ rowIndex: i, email: row.email, name: `${row.firstName} ${row.lastName}`, success: false, error: validationErr, rawRow: row });
      } else {
        try {
          await upsertRow(row);
          batch.push({ rowIndex: i, email: row.email, name: `${row.firstName} ${row.lastName}`, success: true, rawRow: row });
        } catch (err: any) {
          batch.push({ rowIndex: i, email: row.email, name: `${row.firstName} ${row.lastName}`, success: false, error: extractErrorMessage(err), rawRow: row });
        }
      }
      setProgress({ current: i + 1, total: parsedRows.length });
      setResults([...batch]);
    }

    const successCount = batch.filter((r) => r.success).length;
    const failCount    = batch.filter((r) => !r.success).length;
    const uploader = getAdminSession()?.displayName || "Admin";
    logAuditEvent({
      actor:   uploader,
      action:  "bulk_upload",
      label:   "Bulk Order Upload",
      details: `Uploaded ${batch.length} row${batch.length !== 1 ? "s" : ""} from "${fileName}": ${successCount} succeeded, ${failCount} failed`,
    });

    // Save to upload history (fire-and-forget — don't block the UI)
    try {
      const csvHeader = TEMPLATE_HEADERS.join(",");
      const csvRows = parsedRows.map((r) =>
        TEMPLATE_HEADERS.map((h) => {
          const key = HEADER_TO_KEY[h] ?? h;
          const val = String(r[key] ?? "");
          return val.includes(",") || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
        }).join(",")
      );
      const csvContent = [csvHeader, ...csvRows].join("\n");
      await saveUploadHistory({
        uploadedBy: uploader,
        filename: fileName.replace(/\.(xlsx?|ods)$/i, "") + ".csv",
        csvContent,
        results: batch.map((r) => ({
          name: r.name,
          email: r.email,
          package: r.rawRow?.vip_package,
          success: r.success,
          error: r.error,
        })),
      });
      setHistoryKey((k) => k + 1);
    } catch {
      // Non-fatal — don't block the user
    }

    setDone(true);
    setUploading(false);
  };

  // ── Exception download ───────────────────────────────────────────────────────

  const downloadExceptions = () => {
    if (!failures.length) return;
    const headers = [...TEMPLATE_HEADERS, "error_message"];
    const rows = failures.map((r) => {
      const raw = r.rawRow;
      return [
        raw.firstName, raw.lastName, raw.email, raw.phone,
        raw.date_of_birth, raw.sex, raw.address, raw.street_address_2,
        raw.city, raw.state, raw.zip, raw.ghinNumber,
        raw.vip_package, raw.vip_card_type,
        raw.cart_total, raw.payment_method,
        raw.versapayTransactionId, raw.check_number,
        raw.bagTag ? "TRUE" : "FALSE", raw.personalization, raw.bagTagPrice,
        raw.donation ? "TRUE" : "FALSE", raw.donation_amount,
        raw.id || "",
        r.error || "Unknown error",
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = headers.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Exceptions");
    XLSX.writeFile(wb, "vsga_upload_exceptions.xlsx");
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: BRAND_GREEN }}>Bulk Order Upload</h4>
          <p className="text-muted small mb-0 mt-1">Upload a CSV or Excel file to create HubSpot contacts, deals, and line items in bulk.</p>
        </div>
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
          onClick={downloadTemplate}
        >
          <i className="bi bi-download" /> Download Template
        </button>
      </div>

      {/* ── Step 1: Upload ── */}
      {!done && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="fw-semibold small mb-3" style={{ color: BRAND_GREEN, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.73rem" }}>
              Step 1 — Select File
            </div>

            {/* Drop zone */}
            <div
              className="border rounded d-flex flex-column align-items-center justify-content-center"
              style={{ borderStyle: "dashed", borderColor: "#c8cfd8", minHeight: 140, cursor: "pointer", background: parsedRows.length ? "rgba(2,56,34,0.03)" : "#fafafa", transition: "background 0.15s" }}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {parsedRows.length ? (
                <>
                  <i className="bi bi-file-earmark-check-fill" style={{ fontSize: "2rem", color: BRAND_GREEN }} />
                  <div className="mt-2 fw-semibold small" style={{ color: BRAND_GREEN }}>{fileName}</div>
                  <div className="text-muted small">{parsedRows.length} row{parsedRows.length !== 1 ? "s" : ""} ready to upload</div>
                  <button className="btn btn-link btn-sm text-muted mt-1 p-0" onClick={(e) => { e.stopPropagation(); setParsedRows([]); setFileName(""); setResults([]); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                    Remove file
                  </button>
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload" style={{ fontSize: "2rem", color: "#adb5bd" }} />
                  <div className="mt-2 small text-muted">Drop your file here or <span style={{ color: BRAND_GREEN, fontWeight: 600 }}>browse</span></div>
                  <div className="text-muted" style={{ fontSize: "0.72rem" }}>Accepts .csv and .xlsx</div>
                </>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="d-none" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />

            {parseError && <div className="alert alert-danger small mt-3 mb-0 py-2">{parseError}</div>}

            {/* Preview */}
            {parsedRows.length > 0 && (
              <div className="mt-4">
                <div className="fw-semibold small mb-2" style={{ color: BRAND_GREEN, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.73rem" }}>
                  Preview (first 5 rows)
                </div>
                <div className="table-responsive" style={{ fontSize: "0.75rem" }}>
                  <table className="table table-sm table-bordered align-middle mb-0" style={{ whiteSpace: "nowrap" }}>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th className="text-muted fw-semibold">#</th>
                        <th className="text-muted fw-semibold">Name</th>
                        <th className="text-muted fw-semibold">Email</th>
                        <th className="text-muted fw-semibold">Package</th>
                        <th className="text-muted fw-semibold">Cart Total</th>
                        <th className="text-muted fw-semibold">Payment</th>
                        <th className="text-muted fw-semibold">Bag Tag</th>
                        <th className="text-muted fw-semibold">Donation</th>
                        <th className="text-muted fw-semibold">Validation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0, 5).map((r, i) => {
                        const previewErr = validateRow(r, productPrices);
                        return (
                        <tr key={i} style={previewErr ? { backgroundColor: "rgba(220,53,69,0.05)" } : undefined}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{r.firstName} {r.lastName}</td>
                          <td>{r.email}</td>
                          <td><span className="badge text-bg-secondary" style={{ fontWeight: 500 }}>{r.vip_package || "—"}</span></td>
                          <td>${Number(r.cart_total || 0).toFixed(2)}</td>
                          <td>{r.payment_method || "—"}</td>
                          <td>{r.bagTag ? <i className="bi bi-check-circle-fill text-success" /> : <i className="bi bi-x-circle text-muted" />}</td>
                          <td>{r.donation ? <i className="bi bi-check-circle-fill text-success" /> : <i className="bi bi-x-circle text-muted" />}</td>
                          <td style={{ maxWidth: 260, whiteSpace: "normal", lineHeight: 1.3 }}>
                            {previewErr
                              ? <span style={{ color: "#dc3545", fontSize: "0.72rem" }}><i className="bi bi-exclamation-triangle-fill me-1" />{previewErr}</span>
                              : <span style={{ color: "#198754" }}><i className="bi bi-check-circle-fill" /></span>
                            }
                          </td>
                        </tr>
                      )})}
                      {parsedRows.length > 5 && (
                        <tr>
                          <td colSpan={9} className="text-center text-muted" style={{ fontSize: "0.7rem" }}>
                            … and {parsedRows.length - 5} more row{parsedRows.length - 5 !== 1 ? "s" : ""}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Upload progress ── */}
      {parsedRows.length > 0 && (uploading || done) && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="fw-semibold small" style={{ color: BRAND_GREEN, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.73rem" }}>
                {done ? "Upload Complete" : "Uploading…"}
              </div>
              {uploading && (
                <span className="text-muted small">{progress.current} / {progress.total}</span>
              )}
            </div>

            <div className="progress mb-3" style={{ height: 8, borderRadius: 4 }}>
              <div
                className="progress-bar"
                style={{ width: `${pct}%`, backgroundColor: done && failures.length > 0 ? "#f59e0b" : BRAND_GREEN, transition: "width 0.3s" }}
              />
            </div>

            {(uploading || done) && (
              <div className="d-flex gap-4 small">
                <span style={{ color: "#166534" }}>
                  <i className="bi bi-check-circle-fill me-1" style={{ color: "#22c55e" }} />
                  {successes.length} succeeded
                </span>
                <span style={{ color: failures.length > 0 ? "#b02a37" : "#6c757d" }}>
                  <i className={`bi ${failures.length > 0 ? "bi-x-circle-fill" : "bi-dash-circle"} me-1`} style={{ color: failures.length > 0 ? "#dc3545" : "#6c757d" }} />
                  {failures.length} failed
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Results ── */}
      {done && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="fw-semibold small" style={{ color: BRAND_GREEN, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.73rem" }}>
                Results
              </div>
              {failures.length > 0 && (
                <button
                  className="btn btn-sm btn-outline-danger d-flex align-items-center gap-2"
                  style={{ fontSize: "0.78rem" }}
                  onClick={downloadExceptions}
                >
                  <i className="bi bi-download" /> Download Exceptions ({failures.length})
                </button>
              )}
            </div>

            {failures.length === 0 ? (
              <div className="text-center py-3">
                <i className="bi bi-check-circle-fill" style={{ fontSize: "2rem", color: "#22c55e" }} />
                <div className="fw-semibold mt-2" style={{ color: BRAND_GREEN }}>All {successes.length} row{successes.length !== 1 ? "s" : ""} uploaded successfully</div>
              </div>
            ) : (
              <>
                <div className="alert alert-warning small py-2 mb-3">
                  {failures.length} row{failures.length !== 1 ? "s" : ""} failed. Download the exceptions file, fix the errors, and re-upload.
                </div>
                <div className="table-responsive" style={{ maxHeight: 320, overflowY: "auto" }}>
                  <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.78rem" }}>
                    <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0 }}>
                      <tr>
                        <th className="text-muted fw-semibold">Row</th>
                        <th className="text-muted fw-semibold">Name</th>
                        <th className="text-muted fw-semibold">Email</th>
                        <th className="text-muted fw-semibold">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failures.map((r) => (
                        <tr key={r.rowIndex}>
                          <td className="text-muted">{r.rowIndex + 1}</td>
                          <td>{r.name}</td>
                          <td>{r.email}</td>
                          <td style={{ color: "#b02a37" }}>{r.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="mt-3 pt-3 d-flex gap-2" style={{ borderTop: "1px solid #e9ecef" }}>
              <button
                className="btn btn-sm"
                style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none" }}
                onClick={() => { setParsedRows([]); setFileName(""); setResults([]); setDone(false); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              >
                Upload Another File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload button ── */}
      {parsedRows.length > 0 && !uploading && !done && (
        <div className="d-flex justify-content-end">
          <button
            className="btn"
            style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none", minWidth: 160 }}
            onClick={handleUpload}
          >
            <i className="bi bi-cloud-upload me-2" />
            Upload {parsedRows.length} Row{parsedRows.length !== 1 ? "s" : ""}
          </button>
        </div>
      )}

      {/* ── Upload History ── */}
      <hr style={{ margin: "2rem 0 0" }} />
      <UploadHistory refreshKey={historyKey} />
    </div>
  );
};

export default BulkUpload;
